// src/components/forms/SurveyListQuotasItem.js
import React from 'react';
import { UserCog, Target, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

// Expects surveyInfo (matching SurveyInfo Pydantic model) and refreshData function
function SurveyListQuotasItem({ surveyInfo, refreshData }) {
	const surveyTitleDisplay = surveyInfo?.title || 'Untitled Survey';
	const navigate = useNavigate();

	const handleQuotasClick = () => {
		console.log(`Navigating to quotas for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/quotas/${surveyInfo.survey_id}`);
	};

	const handleAudienceConfigClick = () => {
		console.log(`Navigating to audience config for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/audience/${surveyInfo.survey_id}`);
	};

	const handleResponseClick = () => {
		console.log(`Navigating to responses for survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/dashboard/responses/${surveyInfo.survey_id}`);
	};

	// Mock data for quota progression
	const mockQuotaData = [
		{ label: 'Age 18-25', targetCount: 100, currentCount: 87, color: '#3B82F6' },
		{ label: 'Age 26-35', targetCount: 150, currentCount: 142, color: '#8B5CF6' },
		{ label: 'Age 36-50', targetCount: 120, currentCount: 95, color: '#10B981' },
		{ label: 'Age 51+', targetCount: 80, currentCount: 45, color: '#F59E0B' }
	];

	const totalTarget = mockQuotaData.reduce((sum, q) => sum + q.targetCount, 0);
	const totalCurrent = mockQuotaData.reduce((sum, q) => sum + q.currentCount, 0);

	return (
		<div className='border shadow-sm rounded-lg p-6 flex flex-col h-full relative min-h-[320px]'>
			<div className='flex justify-between items-center'>
				<h2 className='text-lg font-semibold text-black flex-grow truncate'>{surveyTitleDisplay}</h2>
				<div className="flex items-center gap-1">
					<span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
				</div>
			</div>

			<h3 className='text-sm text-gray-500 mt-1 mb-4'>
				ID: {surveyInfo.survey_id} - Created: {new Date(surveyInfo.created_at).toLocaleDateString()}
			</h3>

			<hr className='my-2'></hr>

			{/* Quotas Preview */}
			<div className='flex-grow my-4'>
				<div className="flex items-center justify-between mb-3">
					<h4 className='text-md font-medium text-gray-700'>Quota Progress</h4>
					<div className="text-xs text-gray-500 flex items-center">
						<Target className="h-3 w-3 mr-1" />
						Preview
					</div>
				</div>
				
				<div className='bg-gray-50 p-4 rounded-lg border'>
					{/* Quota Progress Bars */}
					<div className="space-y-3">
						{mockQuotaData.map((quota, index) => {
							const percentage = (quota.currentCount / quota.targetCount) * 100;
							const isComplete = quota.currentCount >= quota.targetCount;
							const isNearComplete = percentage >= 90 && !isComplete;
							
							return (
								<div key={index}>
									{/* Label and Count */}
									<div className="flex justify-between items-center mb-1">
										<span className="text-xs font-medium text-gray-700">
											{quota.label}
										</span>
										<span className="text-xs font-semibold" style={{ color: quota.color }}>
											{quota.currentCount} / {quota.targetCount}
										</span>
									</div>
									
									{/* Progress Bar */}
									<div className="bg-gray-200 rounded-full h-3 relative overflow-hidden">
										<div 
											className="h-full rounded-full transition-all duration-500 ease-out relative"
											style={{ 
												width: `${Math.min(percentage, 100)}%`,
												backgroundColor: quota.color
											}}
										>
											{/* Shine effect for near-complete quotas */}
											{isNearComplete && (
												<div 
													className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"
												></div>
											)}
										</div>
										
										{/* Checkmark for complete quotas */}
										{isComplete && (
											<div className="absolute right-1 top-1/2 transform -translate-y-1/2">
												<svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
													<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
												</svg>
											</div>
										)}
									</div>
									
									{/* Percentage */}
									<div className="text-right mt-1">
										<span className={`text-xs font-medium ${isComplete ? 'text-green-600' : 'text-gray-500'}`}>
											{Math.round(percentage)}%
										</span>
									</div>
								</div>
							);
						})}
					</div>
					
					{/* Chart Footer */}
					<div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
						<span className="text-xs text-gray-500">
							Overall: {totalCurrent} / {totalTarget} ({Math.round((totalCurrent / totalTarget) * 100)}%)
						</span>
						<span className="text-xs text-gray-400">Last updated: 15min ago</span>
					</div>
				</div>
			</div>

			<hr className='my-2'></hr>

			{/* Action Buttons - All visible without dropdown */}
			<div className='flex justify-between items-center mt-auto gap-2'>
				{/* Primary Action - Quotas */}
				<Button
					onClick={handleQuotasClick}
					variant="primary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<Target className='h-4 w-4'/> Quotas
				</Button>

				{/* Secondary Actions */}
				<Button
					onClick={handleAudienceConfigClick}
					variant="secondary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<UserCog className='h-4 w-4'/> Audience
				</Button>

				<Button
					onClick={handleResponseClick}
					variant="secondary"
					className="flex gap-2 items-center px-3 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
				>
					<MessageSquare className='h-4 w-4'/> Responses
				</Button>
			</div>
		</div>
	);
}

export default SurveyListQuotasItem;